import React, { memo, useCallback, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
} from 'react-native';
import type { ThreadSortBy } from '@/types/residents/corkBoard';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface NewsfeedSortRowProps {
  postCount: number;
  sortBy: ThreadSortBy;
  onSortChange: (sortBy: ThreadSortBy) => void;
}

export const NewsfeedSortRow: React.FC<NewsfeedSortRowProps> = memo(({
  postCount,
  sortBy,
  onSortChange,
}) => {
  const { colors } = useThemeColors();
  const [isOpen, setIsOpen] = useState(false);
  const currentLabel = CORK_BOARD_SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label ?? 'Sort';

  const handleSelect = useCallback((value: ThreadSortBy) => {
    onSortChange(value);
    setIsOpen(false);
  }, [onSortChange]);

  return (
    <View style={styles.container}>
      <Text style={[styles.postCount, { color: colors.text.dimmer }]}>{postCount} posts</Text>

      <TouchableOpacity
        style={styles.sortTrigger}
        onPress={() => setIsOpen(true)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`Sort by ${currentLabel}`}
        accessibilityState={{ expanded: isOpen }}
      >
        <Text style={styles.sortLabel}>{currentLabel}</Text>
        <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN width={12} height={12} color={PRIMITIVE_COLORS.resioBlue} />
      </TouchableOpacity>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <Pressable style={styles.overlay} onPress={() => setIsOpen(false)}>
          <View style={[styles.dropdown, { backgroundColor: colors.card.bg, borderColor: colors.card.border }]}>
            <Text style={[styles.dropdownTitle, { color: colors.text.primary, borderBottomColor: colors.separator }]}>Sort by</Text>
            {CORK_BOARD_SORT_OPTIONS.map((option) => {
              const isSelected = option.value === sortBy;
              return (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.option, isSelected && { backgroundColor: colors.glass.buttonBg }]}
                  onPress={() => handleSelect(option.value)}
                  activeOpacity={0.7}
                  accessibilityRole="radio"
                  accessibilityLabel={option.label}
                  accessibilityState={{ selected: isSelected }}
                >
                  <Text style={[styles.optionText, { color: colors.text.primary }, isSelected && styles.optionTextSelected]}>
                    {option.label}
                  </Text>
                  {isSelected && (
                    <RESIDENTS_SVG_COMPONENTS.CHECK width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
});

NewsfeedSortRow.displayName = 'NewsfeedSortRow';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  postCount: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  sortTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sortLabel: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    color: PRIMITIVE_COLORS.resioBlue,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  dropdown: {
    borderRadius: 14,
    width: '100%',
    maxWidth: 300,
    paddingVertical: 8,
    borderWidth: 1,
  },
  dropdownTitle: {
    ...REDESIGN_TYPOGRAPHY.postTitle,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionText: {
    fontSize: 15,
  },
  optionTextSelected: {
    color: PRIMITIVE_COLORS.resioBlue,
    fontWeight: '600',
  },
});
